<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import campanhasReportAPI from 'dashboard/api/campanhasReport';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAtendimentoSection } from '../atendimento/useAtendimentoSection';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import CampanhasNumeros from './CampanhasNumeros.vue';
import CampanhasFunil from './CampanhasFunil.vue';
import CampanhaFalhas from './CampanhaFalhas.vue';
import CampanhaLinhaDoTempo from './CampanhaLinhaDoTempo.vue';
import CampanhaTempos from './CampanhaTempos.vue';
import CampanhaDepois from './CampanhaDepois.vue';
import CampanhaConversas from './CampanhaConversas.vue';
import CampanhaDescadastros from './CampanhaDescadastros.vue';
import CampanhaCusto from './CampanhaCusto.vue';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { campanhasKeys, campanhasLabel } from './campanhasLabels';

// O detalhe de uma campanha: é o filtro `campanha` com exatamente uma chave
// (PLAN D8), e não um estado à parte. "Voltar" tira a chave e mantém o resto
// do filtro — quem escreve é a aba. Cada seção pede a própria rota, com o
// recorte da página; a linha da tabela dá o nome, e o detalhe operacional
// (`operational`), que só vem para o super admin (D18). O custo também é só dele:
// para os outros, a seção nem pede.
const props = defineProps({
  // O recorte da API (`campanhasApiFilters`), com uma chave só.
  filters: { type: Object, required: true },
  // A opção da campanha (`campanhas/opcoes`), para o nome antes da tabela.
  campaign: { type: Object, default: null },
});

const emit = defineEmits(['back']);

const { t } = useI18n();
const { number, dateTime } = useAnalyticsFormat();
const route = useRoute();

const campaignKey = computed(() => props.filters.campaignKeys?.[0]);

const resumo = useAtendimentoSection(f => campanhasReportAPI.resumo(f));
const falhas = useAtendimentoSection(f => campanhasReportAPI.falhas(f));
const linhaDoTempo = useAtendimentoSection(f =>
  campanhasReportAPI.linhaDoTempo(f)
);
const tabela = useAtendimentoSection(f =>
  campanhasReportAPI.tabela(f, { page: 1 })
);

watch(
  () => JSON.stringify(props.filters),
  key => {
    [resumo, falhas, linhaDoTempo, tabela].forEach(section =>
      section.loadIfStale(key, props.filters)
    );
  },
  { immediate: true }
);

const row = computed(
  () =>
    (tabela.data.value?.rows || []).find(
      item => item.key === campaignKey.value
    ) || null
);
const timezone = computed(() => tabela.data.value?.period?.timezone);

const title = computed(
  () => row.value?.title || props.campaign?.title || campaignKey.value
);
const details = computed(() => {
  const source = row.value || props.campaign || {};
  return [
    campanhasLabel(t, 'kind', source.kind),
    source.template,
    source.segment,
    row.value?.agent?.name,
    row.value?.inbox,
  ]
    .filter(Boolean)
    .join(' · ');
});
const sentRange = computed(() => {
  if (!row.value?.first_sent_at) return '';
  const first = dateTime(row.value.first_sent_at, timezone.value);
  const last = dateTime(row.value.last_sent_at, timezone.value);
  return first === last
    ? t('CAMPANHAS.DETAIL.SENT_AT', { date: first })
    : t('CAMPANHAS.DETAIL.SENT_BETWEEN', { first, last });
});

// O detalhe operacional só aparece quando a chave vem: o servidor a manda só
// para o super admin, e só na campanha (o lote não tem esse detalhe).
const operational = computed(() =>
  row.value?.operational?.display_id
    ? {
        name: 'campaigns_detail',
        params: {
          accountId: route.params.accountId,
          campaignId: row.value.operational.display_id,
        },
      }
    : null
);

// As linhas que abrem a lista das conversas, com o número do resumo quando ele
// o tem ("não responderam" não tem: a lista diz quantas são).
const conversasRef = ref(null);
const AFTER = campanhasKeys('after');
const lineCount = key => {
  const report = resumo.data.value;
  if (!report) return null;
  if (['replied', 'failed', 'opted_out'].includes(key))
    return report.numbers?.[key] ?? null;
  if (AFTER.includes(key)) return report.after_reply?.[key]?.count ?? null;
  return null;
};
const lines = computed(() =>
  campanhasKeys('line').map(key => ({
    key,
    label: campanhasLabel(t, 'line', key),
    count: lineCount(key),
  }))
);
const openLine = line => conversasRef.value?.open(line.key, line.label);
const reportTimezone = computed(
  () => resumo.data.value?.period?.timezone || timezone.value
);

// A tabela respondeu, e a campanha não teve envio neste recorte.
const noSends = computed(() => Boolean(tabela.data.value) && !row.value);
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0" data-test-id="campanha-detalhe">
    <header class="flex flex-col gap-2 min-w-0">
      <div>
        <Button
          data-test-id="campanha-voltar"
          :label="t('CAMPANHAS.DETAIL.BACK')"
          icon="i-lucide-arrow-left"
          xs
          slate
          faded
          type="button"
          @click="emit('back')"
        />
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 justify-between items-start">
        <div class="flex flex-col gap-0.5 min-w-0">
          <h2
            data-test-id="campanha-titulo"
            class="break-words text-heading-2 text-n-slate-12"
          >
            {{ title }}
          </h2>
          <p v-if="details" class="mb-0 text-sm text-n-slate-11">
            {{ details }}
          </p>
          <p v-if="sentRange" class="mb-0 text-xs text-n-slate-10">
            {{ sentRange }}
          </p>
        </div>
        <router-link
          v-if="operational"
          data-test-id="campanha-operacional"
          :to="operational"
          class="text-sm font-medium whitespace-nowrap text-n-brand hover:underline"
        >
          {{ t('CAMPANHAS.DETAIL.OPERATIONAL') }}
        </router-link>
      </div>
      <p
        v-if="noSends"
        data-test-id="campanha-sem-envio"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('CAMPANHAS.DETAIL.NO_SENDS') }}
      </p>
    </header>

    <CampanhasNumeros
      :report="resumo.data.value"
      :failed="resumo.failed.value"
    />
    <CampanhasFunil :report="resumo.data.value" :failed="resumo.failed.value" />
    <CampanhaFalhas :report="falhas.data.value" :failed="falhas.failed.value" />
    <CampanhaLinhaDoTempo
      :report="linhaDoTempo.data.value"
      :failed="linhaDoTempo.failed.value"
    />
    <CampanhaTempos
      :times="resumo.data.value?.times || null"
      :failed="resumo.failed.value"
    />
    <CampanhaDepois
      :after="resumo.data.value?.after_reply || null"
      :failed="resumo.failed.value"
    />
    <AnalyticsPanel
      data-test-id="campanha-conversas"
      :title="t('CAMPANHAS.CONVERSATIONS.PANEL_TITLE')"
      :description="t('CAMPANHAS.CONVERSATIONS.PANEL_DESCRIPTION')"
    >
      <ul class="grid gap-x-6 m-0 list-none sm:grid-cols-2">
        <li v-for="line in lines" :key="line.key" class="min-w-0">
          <button
            type="button"
            :data-test-id="`campanha-linha-${line.key}`"
            class="flex gap-3 justify-between items-baseline py-1.5 w-full text-sm rounded-md text-start hover:bg-n-alpha-1"
            @click="openLine(line)"
          >
            <span class="text-n-slate-12">{{ line.label }}</span>
            <span
              v-if="typeof line.count === 'number'"
              class="font-medium tabular-nums text-n-slate-12"
            >
              {{ number(line.count) }}
            </span>
          </button>
        </li>
      </ul>
    </AnalyticsPanel>
    <CampanhaDescadastros :filters="filters" :timezone="reportTimezone" />
    <CampanhaCusto :filters="filters" />
    <CampanhaConversas
      ref="conversasRef"
      :filters="filters"
      :timezone="reportTimezone"
    />
  </div>
</template>
